import {RowDescriptorRenderProps} from "./types";
import {useUserEmailsQuery} from "./queries.generated";
import {FormControl, FormHelperText, MenuItem, Select, Typography} from "@mui/material";
import {MultipleLineError} from "@dvrt/graphql-widgets";
import {useCallback} from "react";
import {FormattedMessage} from "react-intl";
import {commonTranslations} from "@/utils/translations";

/// Item for the Select component representing an email and its validity status.
type EmailOption = {
    email: string;
    valid: boolean;
};

/// A registry mapping email addresses to their validity status for quick lookup.
type EmailValidityRegistry = Record<string, boolean>;

type DataSource = {
    options: EmailOption[];
    isValid: (email: string) => boolean;
};

const useDataSource = (current: string | undefined | null): DataSource => {
    const {data} = useUserEmailsQuery();

    const emails: EmailValidityRegistry = Object.values(data?.userEmails ?? []).reduce((acc, email) => {
        if (email) acc[email] = true;
        return acc;
    }, {} as EmailValidityRegistry);

    const isValid = useCallback((email: string) => emails[email] ?? false, [emails]);

    if (!data) return {options: [{email: current ?? "", valid: true}], isValid: () => true};

    if (current && !emails[current]) {
        emails[current] = false;
    }

    const options: EmailOption[] = Object.entries(emails).map(([email, valid]) => ({email, valid}));

    return {options, isValid};
};

export const LimitedNotificationEmailWidget = (props: RowDescriptorRenderProps<"notificationEmail">) => {
    const {disabled, externalDisabled, updateValue, value, errors} = props;
    const hasErrors = errors && errors.length > 0;

    const emails = useDataSource(value);

    return (
        <FormControl error={hasErrors}>
            <Select
                disabled={disabled || externalDisabled}
                fullWidth
                variant="outlined"
                margin="dense"
                value={value}
                onChange={(event) => updateValue(event.target.value)}
                renderValue={(value) => {
                    if (value === "") {
                        return <FormattedMessage {...commonTranslations.notSet} />;
                    }

                    return <Typography sx={{color: !emails.isValid(value) ? "error.main" : null}}>{value}</Typography>;
                }}
                displayEmpty
            >
                <MenuItem key="not-set" value="">
                    <FormattedMessage {...commonTranslations.notSet} />
                </MenuItem>
                {emails.options.map((item) => (
                    <MenuItem
                        key={item.email}
                        value={item.email}
                        sx={{
                            color: !item.valid ? "error.main" : null,
                        }}
                    >
                        {item.email}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{hasErrors && <MultipleLineError errors={errors} />}</FormHelperText>
        </FormControl>
    );
};
